/**
 * SearchProductGrid is a custom element that handles the display and lazy loading of products in search results.
 * It manages product loading states and implements lazy loading through Intersection Observer.
 * @extends {window.BAO.CustomElement}
 */
class SearchProductGrid extends window.BAO.CustomElement() {
  /**
   * Creates an instance of SearchProductGrid.
   * Initializes state and event bus for autocomplete search results.
   */
  constructor () {
    super()

    this.eventBus = window.theme.autocompleteResults.eventBus
    this.state = {
      root: window.theme.autocompleteResults?.stateLayer?.state || {
        products: [],
      },
      observer: null,
    }

    this.productList = null
    this.productCache = new Map()
    this.currentSearchTerm = ''
  }

  /**
   * Lifecycle callback when the element is added to the DOM.
   * Subscribes to product update events.
   */
  connectedCallback () {
    this.productList = this.querySelector('.prd-List_Items')
    this.subscribeToEvents()
  }

  /**
   * Lifecycle callback when the element is removed from the DOM.
   * Cleans up event listeners and disconnects the intersection observer.
   */
  disconnectedCallback () {
    if (this.observer) {
      this.observer.disconnect()
    }
    this.eventBus.unsubscribe('state:products:updated')
  }

  /**
   * Subscribes to product update events from the event bus.
   * Handles both empty product lists and product updates.
   */
  subscribeToEvents () {
    // Clear products immediately when user starts typing/backspacing
    this.eventBus.subscribe('search:clear:immediate', () => {
      // Rob says this makes the search feel slow.
      // this.clearProducts()
    })

    // Clear products immediately when a new search starts
    this.eventBus.subscribe('state:search:term', term => {
      // Update current search term and clear products
      this.currentSearchTerm = term
      this.clearProducts()
    })

    this.eventBus.subscribe('state:products:updated', products => {
      this.clearEmptyProducts()

      // Verify these products are for the current search term
      const searchTerm = window.theme.autocompleteResults?.searchTerm || ''
      if (searchTerm !== this.currentSearchTerm) {
        // These products are for a different search - ignore them
        return
      }

      if (products.length === 0) {
        // Only show empty message if there's an active search term
        if (searchTerm) {
          this.handleEmptyProducts()
          // Pagination will be hidden by pagination-control based on totalProducts
        } else {
          // Search was reset/cleared - just ensure products and pagination are hidden
          this.clearProducts()
        }
        return
      }

      // Ensure products are cleared before showing new ones
      if (this.productList) {
        this.hidePagination()
        this.productList.innerHTML = ''
      }

      // Show product list, pagination, count, and quick links when new products arrive
      if (this.productList) {
        this.productList.removeAttribute('hidden')
      }
      this.showPagination()
      this.showProductCount()
      this.showQuickLinks()

      this.handleProductUpdate(products)
      this.setResultsReady(true)
    })

    // Scroll to top when pagination is triggered
    this.eventBus.subscribe('pagination:control:load-more', () => {
      this.scrollResultsToTop()
    })

    this.eventBus.subscribe('pagination:control:load-previous', () => {
      this.scrollResultsToTop()
    })
  }

  /**
   * Clears all products from the grid.
   * Used when a new search starts or when search is reset.
   */
  clearProducts () {
    const headerSearch = this.closest('header-search')
    if (headerSearch?.dataset.quicklinkInput === 'true') {
      return
    }

    // Uncomment this if you want to hide the flash of the header (filters / sort) when typing a search term with no results.
    // The knock-on of this is that the results panel will be hidden when typing any search term, even if there are results.
    // This has the psychological effect of making the search feel slower.
    // this.setResultsReady(false)

    if (this.productList) {
      this.productList.innerHTML = ''
      this.productList.setAttribute('hidden', '')
    }
    this.hidePagination()
    this.hideProductCount()
    this.hideQuickLinks()
  }

  /**
   * Sets whether the search results container is ready to display.
   * @param {boolean} isReady - Whether results are populated.
   */
  setResultsReady (isReady) {
    const resultsContainer = this.closest('[data-header-search-el="results"]')
    if (!resultsContainer) {
      return
    }
    resultsContainer.setAttribute(
      'data-results-ready',
      isReady ? 'true' : 'false',
    )
  }

  /**
   * Hides the pagination controls.
   */
  hidePagination () {
    const pagination = document.querySelector(
      '[data-search-product-grid-external-el="pagination"]',
    )
    if (pagination) {
      pagination.setAttribute('hidden', '')
    }
  }

  /**
   * Shows the pagination controls (pagination control will handle visibility based on totalProducts).
   */
  showPagination () {
    const pagination = document.querySelector(
      '[data-search-product-grid-external-el="pagination"]',
    )
    if (pagination) {
      pagination.removeAttribute('hidden')
    }
  }

  /**
   * Hides the product count elements (mobile and desktop).
   */
  hideProductCount () {
    const mobileCount = document.querySelector(
      '.clc-ProductGridMobile_Count[role="status"]',
    )
    const desktopCount = document.querySelector(
      '.clc-ProductGrid_Count[role="status"]',
    )
    if (mobileCount) {
      mobileCount.setAttribute('hidden', '')
    }
    if (desktopCount) {
      desktopCount.setAttribute('hidden', '')
    }
  }

  /**
   * Shows the product count elements (mobile and desktop).
   */
  showProductCount () {
    const mobileCount = document.querySelector(
      '.clc-ProductGridMobile_Count[role="status"]',
    )
    const desktopCount = document.querySelector(
      '.clc-ProductGrid_Count[role="status"]',
    )
    if (mobileCount) {
      mobileCount.removeAttribute('hidden')
    }
    if (desktopCount) {
      desktopCount.removeAttribute('hidden')
    }
  }

  /**
   * Hides the quick links elements (mobile and desktop).
   */
  hideQuickLinks () {
    const quickLinks = document.querySelectorAll(
      'quick-links[data-type="autocomplete"]',
    )
    quickLinks.forEach(link => {
      link.setAttribute('hidden', '')
    })
  }

  /**
   * Shows the quick links elements (mobile and desktop).
   */
  showQuickLinks () {
    const quickLinks = document.querySelectorAll(
      'quick-links[data-type="autocomplete"]',
    )
    quickLinks.forEach(link => {
      link.removeAttribute('hidden')
    })
  }

  /**
   * Scrolls the search results container to the top.
   */
  scrollResultsToTop () {
    const searchResults = document.querySelector(
      '[data-header-search-el="results"]',
    )
    if (searchResults) {
      searchResults.scrollTop = 0
    }
  }

  /**
   * Removes the empty products message from the DOM.
   */
  clearEmptyProducts () {
    const emptyText = this.querySelector('[data-empty-text]')
    if (emptyText) {
      emptyText.remove()
    }
  }

  /**
   * Handles the case when no products are found.
   * Displays a message and hides the product list.
   */
  handleEmptyProducts () {
    this.insertAdjacentHTML(
      'beforeend',
      `
      <p class="clc-ProductGrid_Text clc-ProductGrid_Text-empty fz-35_60" data-empty-text>
        ${window.theme.strings.collection.empty} "${this.currentSearchTerm}"
      </p>
    `,
    )
    this.setResultsReady(true)

    this.querySelector('.prd-List_Items').setAttribute('hidden', '')
  }

  /**
   * Handles product updates and orchestrates the update process.
   * @param {Array<Object>} products - Array of product objects to display
   */
  handleProductUpdate (products) {
    const urlParams = this.getUrlParameters()
    this.updateProductListVisibility()
    this.renderProductSkeletons(products)
    this.buildKlevuCards(products)

    if (this.hasUrlParameters(urlParams)) {
      this.handleUrlParameters(products, urlParams)
    } else {
      this.handleInitialLoad(products)
    }
  }

  /**
   * Extracts URL parameters from the current page URL.
   * @returns {Object} Object containing page, sortBy, filters, and q parameters
   */
  getUrlParameters () {
    const url = new URL(window.location.href)
    return {
      page: url.searchParams.get('auto_page'),
      sortBy: url.searchParams.get('sort_by'),
      filters: url.searchParams.get('search_filters'),
      q: url.searchParams.get('q'),
    }
  }

  /**
   * Checks if any URL parameters are present.
   * @param {Object} params - URL parameters object
   * @returns {boolean} True if any parameters are present
   */
  hasUrlParameters (params) {
    return params.page || params.sortBy || params.filters || params.q
  }

  /**
   * Updates the visibility of the product list container.
   * Clears contents first (while hidden), then makes the list visible.
   */
  updateProductListVisibility () {
    const productList = this.querySelector('.prd-List_Items')
    // Clear innerHTML first while still hidden to prevent flash of old content
    productList.innerHTML = ''
    // Then remove hidden attribute to show the cleared list
    productList.removeAttribute('hidden')
  }

  /**
   * Renders skeleton loading placeholders for products.
   * @param {Array<Object>} products - Array of product objects
   */
  renderProductSkeletons (products) {
    const fragment = document.createDocumentFragment()

    products.forEach(product => {
      const li = document.createElement('li')

      if (
        window.location.hash &&
        window.location.hash.startsWith('#wishlist-')
      ) {
        li.setAttribute('id', `wishlist-${product.id}`)
      } else {
        li.setAttribute('id', `product-${product.id}`)
      }

      li.classList.add('prd-List_Item')
      li.setAttribute('data-search-product-grid-el', 'item')
      li.setAttribute('data-loading', 'true')
      li.innerHTML = this.createSkeletonHTML()

      fragment.appendChild(li)
    })

    this.productList.appendChild(fragment)
  }

  /**
   * Creates HTML for a product skeleton loading placeholder.
   * @returns {string} HTML string for the skeleton
   */
  createSkeletonHTML () {
    return `
      <div class="prd-CardSkeleton">
        <div class="prd-CardSkeleton_Image"></div>
        <div class="prd-CardSkeleton_Body"></div>
      </div>
    `
  }

  /**
   * Handles product updates when URL parameters are present.
   * @param {Array<Object>} products - Array of product objects
   * @param {Object} urlParams - URL parameters object
   */
  handleUrlParameters (products, urlParams) {
    if (this.observer) {
      this.observer.disconnect()
    }

    this.els.item.elements.forEach(item => {
      item.removeAttribute('data-url')
    })

    this.attachProductUrls(products)
    this.resetLoadingState()
    this.intersectionObserver()

    if (
      window.location.hash &&
      (window.location.hash.startsWith('#product-') ||
        window.location.hash.startsWith('#wishlist-'))
    ) {
      return
    }

    if (urlParams.page) {
      // Scroll search results container to top when pagination changes
      const searchResults = document.querySelector(
        '[data-header-search-el="results"]',
      )
      if (searchResults) {
        searchResults.scrollTop = 0
      } else {
        // Fallback to window scroll if container not found
        window.scrollTo({
          top: 0,
          behavior: 'instant',
        })
      }
    }
  }

  /**
   * Handles initial product load without URL parameters.
   * @param {Array<Object>} products - Array of product objects
   */
  handleInitialLoad (products) {
    this.attachProductUrls(products)
    this.intersectionObserver()
  }

  /**
   * Resets the loading state for all product items.
   */
  resetLoadingState () {
    this.els.item.elements.forEach(item => {
      item.dataset.loading = true
    })
  }

  /**
   * Sets up the Intersection Observer for lazy loading products.
   * Loads products when they come into view.
   */
  intersectionObserver () {
    const options = {
      root: null,
      rootMargin: '800px',
      threshold: 0,
    }

    this.observer = new IntersectionObserver(entries => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          if (entry.target.dataset.loading === 'true') {
            entry.target.dataset.loading = false

            this.fetchProductWithRetry(entry.target.dataset.url, index)
              .then(html => {
                entry.target.innerHTML = html
              })
              .catch(error => {
                console.error(
                  `Error fetching product ${entry.target.dataset.url}:`,
                  error,
                )
              })
          }
        }
      })
    }, options)

    this.els.item.elements.forEach(item => {
      this.observer.observe(item)
    })
  }

  /**
   * Builds and renders Klevu product cards for the first 8 products.
   * @param {Array<Object>} products - Array of product objects
   */
  buildKlevuCards (products) {
    const productsToRender = products.slice(0, 8)
    const items = this.els.item.elements

    if (products.length < items.length) {
      for (let i = products.length; i < items.length; i++) {
        items[i].style.display = 'none'
      }
    } else {
      items.forEach(item => {
        item.style.display = ''
      })
    }

    productsToRender.forEach((product, index) => {
      items[index].dataset.productName = product.name

      items[index].innerHTML = `
        <div class="prd-Card">
          <div class="prd-Card_MediaContainer">
            <img
              src="${product.image}"
              alt="${product.name}"
              style="aspect-ratio: 650 / 840; object-fit: cover;"
            >
          </div>
          <div class="prd-Card_Body">
            <div class="prd-Card_Title fz-15_18">
              ${product.name}
            </div>
            <div class="prd-Card_Price fz-15_35" style="height: 25px;">
            </div>
          </div>
        </div>
      `
    })
  }

  /**
   * Attaches product URLs to the grid items.
   * @param {Array<Object>} products - Array of product objects
   */
  attachProductUrls (products) {
    const items = this.els.item.elements
    const currentPath = window.location.pathname
    const localeMatch = currentPath.match(/^\/([a-z]{2}-[a-z]{2})(\/|$)/)
    const localePrefix = localeMatch ? `/${localeMatch[1]}` : ''

    products.forEach((product, index) => {
      const handle = product.url.split('/').pop()
      const url = `${localePrefix}/products/${handle}`

      if (url) {
        items[index].setAttribute('data-url', url)
      } else {
        items[index].setAttribute('data-url', '')
      }
    })
  }

  async fetchProduct (url, index) {
    if (!url) {
      console.error('No URL provided for product fetch')
      return null
    }

    const currentPath = window.location.pathname
    const localeMatch = currentPath.match(/^\/([a-z]{2}-[a-z]{2})(\/|$)/)

    if (
      localeMatch &&
      localeMatch[1] &&
      !url.startsWith(`/${localeMatch[1]}`)
    ) {
      const localePrefix = `/${localeMatch[1]}`
      url = `${localePrefix}${url}`
    }

    if (this.productCache.has(url)) {
      return this.productCache.get(url)
    }

    const section = 'search-product-card'

    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)
    this.productCache.set(url, html)
    return html
  }

  async fetchProductWithRetry (url, index, retries = 3) {
    for (let i = 0; i < retries; i++) {
      try {
        return await this.fetchProduct(url, index)
      } catch (error) {
        if (i === retries - 1) throw error
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000))
      }
    }
  }
}

customElements.define('search-product-grid', SearchProductGrid)
